import { apiGet, apiPost, apiRequest, clearApiGetCache } from './apiClient';
import { clearPageCache } from '../utils/pageDataCache';
import { LIVE_PAGE_CACHE_KEYS } from '../utils/livePageCacheKeys';

/** Watchlist mutations must invalidate ST/LT page caches, not just the GET memo,
 * or pages that hydrate from sessionStorage keep showing pre-mutation rows. */
const invalidateWatchlistPageCaches = () => {
  clearApiGetCache();
  clearPageCache(LIVE_PAGE_CACHE_KEYS.shortTermWatchlist);
  clearPageCache(LIVE_PAGE_CACHE_KEYS.longTermWatchlist);
};

const extractRows = (payload, keys = []) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  for (const key of keys) {
    if (Array.isArray(payload?.[key])) return payload[key];
  }
  if (Array.isArray(payload?.result)) return payload.result;
  if (Array.isArray(payload?.items)) return payload.items;
  return [];
};

export const fetchWatchlist = async (listType = null, options = {}) => {
  const includeAll = Boolean(options?.includeAll);
  let url = '/watchlist';
  const params = new URLSearchParams();
  if (listType) params.set('list_type', listType);
  if (includeAll) params.set('include_all', 'true');
  const qs = params.toString();
  if (qs) url += `?${qs}`;
  const data = await apiGet(url, { skipCache: options?.skipCache === true });
  return extractRows(data, ['watchlist', 'rows']);
};

export const addToWatchlist = async (symbol, listType = 'long_term', notes = '') => {
  const res = await apiPost('/watchlist', { symbol, list_type: listType, notes });
  invalidateWatchlistPageCaches();
  return res;
};

/** Samco candles + technical + rating for symbols already on your watchlist (any list). */
export const backfillWatchlistMarketData = async (symbols) =>
  apiPost('/watchlist/backfill-market-data', { symbols });

export const fetchWatchlistFundamentals = async (symbol) =>
  apiGet(`/watchlist/fundamentals/${encodeURIComponent(symbol)}`);

export const refreshWatchlistFundamentals = async (symbols) =>
  apiPost('/watchlist/refresh-fundamentals', { symbols: symbols || [] });

export const removeFromWatchlist = async (symbol, listType = 'long_term', options = {}) => {
  const includeAll = Boolean(options?.includeAll);
  const params = new URLSearchParams();
  params.set('list_type', listType);
  if (includeAll) params.set('include_all', 'true');
  const res = await apiRequest(`/watchlist/symbol/${encodeURIComponent(symbol)}?${params.toString()}`, { method: 'DELETE' });
  invalidateWatchlistPageCaches();
  return res;
};

export const bulkDeleteFromWatchlist = async (symbols, listType, options = {}) => {
  const includeAll = Boolean(options?.includeAll);
  const qs = includeAll ? '?include_all=true' : '';
  const res = await apiPost(`/watchlist/bulk-delete${qs}`, { symbols, list_type: listType });
  invalidateWatchlistPageCaches();
  return res;
};

export const updateWatchlistEntry = async (symbol, data) => {
  return apiRequest(`/watchlist/symbol/${encodeURIComponent(symbol)}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
};

export const fetchWatchlistSignals = async (options = {}) => {
  const includeAll = Boolean(options?.includeAll);
  const params = new URLSearchParams();
  if (includeAll) params.set('include_all', 'true');
  if (options?.timeframe) params.set('timeframe', String(options.timeframe).toLowerCase());
  const qs = params.toString();
  const data = await apiGet(`/watchlist/signals${qs ? `?${qs}` : ''}`);
  return extractRows(data, ['signals', 'rows']);
};

export const fetchWeeklyIndicators = async (options = {}) => {
  const includeAll = Boolean(options?.includeAll);
  const data = await apiGet(`/watchlist/weekly-indicators${includeAll ? '?include_all=true' : ''}`);
  return extractRows(data, ['weekly_indicators', 'weekly_entries', 'rows']);
};

export const fetchOrderBlocks = async (options = {}) => {
  const includeAll = Boolean(options?.includeAll);
  const data = await apiGet(`/watchlist/order-blocks${includeAll ? '?include_all=true' : ''}`);
  return extractRows(data, ['order_blocks', 'rows']);
};
